// India's states and the cities in them, from `indian-cities-json` (MIT).
//
// The package ships one flat list of `{ name, state }`; this is where it
// becomes the two lookups the address fields need, and where its known data
// faults are corrected. Everything is derived once at module load — the list
// is static, so rebuilding it per render would be pure waste.
//
// Shared rather than owned by a feature: the brand address and the client
// address ask the same question, and two copies of the country would be two
// copies to correct.

import { cities } from "indian-cities-json";

/**
 * Spellings the dataset gets wrong. Left alone, "Himachal Praddesh" appears
 * in the dropdown as a 35th state holding a single city.
 */
const STATE_CORRECTIONS: Record<string, string> = {
  "Himachal Praddesh": "Himachal Pradesh",
};

const CITIES_BY_STATE = cities.reduce<Record<string, string[]>>(
  (byState, city) => {
    const state = STATE_CORRECTIONS[city.state] ?? city.state;
    (byState[state] ||= []).push(city.name);
    return byState;
  },
  {}
);

// The list carries a handful of repeated entries, so each state's cities are
// deduped as well as sorted.
Object.keys(CITIES_BY_STATE).forEach((state) => {
  CITIES_BY_STATE[state] = Array.from(new Set(CITIES_BY_STATE[state])).sort();
});

/** Every state and union territory the dataset knows a city in. */
export const STATE_OPTIONS = Object.keys(CITIES_BY_STATE).sort();

/** One shared empty list, so the no-state case is a stable reference too. */
const NO_CITIES: string[] = [];

/**
 * Empty for a state that is unset or unrecognised — which is what leaves the
 * city dropdown with nothing to offer until a state has been picked.
 *
 * The array returned is the stored one, not a copy: callers memoise on its
 * identity, and handing back a fresh array per call would defeat that.
 */
export const citiesOfState = (state: string): string[] =>
  CITIES_BY_STATE[state.trim()] ?? NO_CITIES;

/**
 * The state a city is in, built once by reading the lookup above backwards.
 *
 * For a form that asks for a city and not for a state — the public enquiry
 * form, where every field is one a stranger has to be willing to fill in, and
 * "which state is Mumbai in" is a question the app can answer for them.
 *
 * A handful of city names occur in more than one state. The first one wins,
 * which is a guess — but a guess on a field nobody was asked about is better
 * than an extra dropdown, and the practitioner can correct it on the record
 * afterwards.
 */
const STATE_BY_CITY: Record<string, string> = {};
Object.entries(CITIES_BY_STATE).forEach(([state, cities]) => {
  cities.forEach((city) => {
    const key = city.toLowerCase();
    if (!(key in STATE_BY_CITY)) STATE_BY_CITY[key] = state;
  });
});

/**
 * Every city in the country, sorted — one flat list for a city-only field.
 *
 * Deduped across states as well as within them. The lists above are deduped
 * one state at a time, which leaves a name that occurs in two of them — and a
 * hundred-odd do — appearing twice here. On a field that asks only for a city
 * those two rows are indistinguishable, so one of them is noise; and being
 * identical they are also the same option twice, which is a broken list rather
 * than an untidy one. React keys a list by what is in it, so two rows claiming
 * the same name reconcile as one and the rows around them come back in an
 * order nobody asked for. That is what a reopened City field looked like: the
 * first sixty rows hold no repeats and read perfectly, and a list paged deep
 * enough to reach Wardha came back as a handful of the duplicated names.
 */
export const ALL_CITIES: string[] = Array.from(
  new Set(Object.values(CITIES_BY_STATE).flat())
).sort();

/** The state holding a city, or an empty string when the name is unknown. */
export const stateOfCity = (city: string): string =>
  STATE_BY_CITY[city.trim().toLowerCase()] ?? "";
